import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import { AlertTriangle, PhoneOff } from 'lucide-react';
import { Button } from './Button';

type ConfirmActionModalTone = 'danger' | 'neutral';

type ConfirmActionModalProps = {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: () => void;
	title: ReactNode;
	description: ReactNode;
	confirmButtonText: string;
	disableConfirm?: boolean;
	zIndex?: number;
	tone?: ConfirmActionModalTone;
	tutorialDemoTarget?: string;
};

export const ConfirmActionModal = ({
	isOpen,
	onClose,
	onConfirm,
	title,
	description,
	confirmButtonText,
	disableConfirm = false,
	zIndex = 10020,
	tone = 'danger',
	tutorialDemoTarget,
}: ConfirmActionModalProps) => {
	if (!isOpen) return null;

	const isNeutralTone = tone === 'neutral';

	return createPortal(
		<div
			className="fixed inset-0 flex items-center justify-center p-4"
			style={{ zIndex }}
		>
			<div
				className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"
				onClick={onClose}
			/>
			<div
				{...(tutorialDemoTarget
					? { 'data-tutorial-demo': tutorialDemoTarget }
					: {})}
				className="animate-in zoom-in-95 relative w-full max-w-[400px] rounded-[2rem] bg-white p-8 text-center shadow-2xl duration-200"
			>
				<div
					className={
						isNeutralTone
							? 'mb-4 flex justify-center text-slate-500'
							: 'mb-4 flex justify-center text-rose-500'
					}
				>
					<div
						className={
							isNeutralTone
								? 'rounded-full bg-slate-100 p-4'
								: 'rounded-full bg-rose-50 p-4'
						}
					>
						{isNeutralTone ? (
							<PhoneOff size={32} />
						) : (
							<AlertTriangle size={32} />
						)}
					</div>
				</div>

				<h3 className="mb-2 text-xl font-black text-stone-900">
					{title}
				</h3>

				<div className="mb-8 text-sm font-medium leading-relaxed text-stone-500">
					{description}
				</div>

				<div className="flex flex-col gap-3">
					<Button
						type="button"
						text={confirmButtonText}
						className="w-full justify-center rounded-full border-none bg-zinc-900 py-3 font-bold text-white shadow-md hover:bg-zinc-800 disabled:opacity-60"
						onClick={onConfirm}
						disabled={disableConfirm}
					/>

					<button
						type="button"
						onClick={onClose}
						disabled={disableConfirm}
						className="text-sm font-bold text-stone-400 transition-colors hover:text-stone-600 disabled:opacity-50"
					>
						Cancelar
					</button>
				</div>
			</div>
		</div>,
		document.body,
	);
};
